import React, { useEffect, useState, useMemo } from 'react';
import styles from './Starfield.module.css';

/**
 * Starfield Component - Animated Background
 * DESKTOP-FIRST: Parallax activo en 1920px+
 * OPTIMIZED: Densidad reducida en tablets, desactivado en móvil
 */
const Starfield = ({ density = 100 }) => {
  const [parallaxOffset, setParallaxOffset] = useState({ x: 0, y: 0 });
  
  // ⚡ Calcular densidad óptima basada en viewport (solo una vez)
  const optimizedDensity = useMemo(() => {
    const width = window.innerWidth;
    if (width <= 768) return 0; // Desactivado en móvil (manejado en App.js)
    if (width <= 1024) return Math.min(density, 50); // Tablets: máx 50 estrellas
    if (width <= 1440) return Math.min(density, 80); // Laptops: máx 80 estrellas
    return density; // Desktop: densidad completa
  }, [density]);

  // ⚡ Generar estrellas - memoizado
  const stars = useMemo(() => {
    const starArray = [];
    for (let i = 0; i < optimizedDensity; i++) {
      starArray.push({
        id: i,
        left: Math.random() * 100,
        top: Math.random() * 100,
        size: Math.random() * 2.5 + 0.5, // Reducido ligeramente
        animationDuration: Math.random() * 2 + 3, // 3-5s (más lento = menos GPU)
        animationDelay: Math.random() * 2,
        depth: Math.random(),
      });
    }
    return starArray;
  }, [optimizedDensity]);

  // Efecto parallax con mouse (solo en desktop) con throttle para performance
  useEffect(() => {
    let lastMoveTime = 0;
    const throttleMs = 32; // ~30fps (reducido para mejor performance)
    
    const handleMouseMove = (e) => {
      const now = Date.now();
      if (now - lastMoveTime < throttleMs) return;
      lastMoveTime = now;
      
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      if (window.innerWidth < 1024) return; // Parallax solo en desktop grande

      const x = (e.clientX / window.innerWidth - 0.5) * 15; // Reducido de 20
      const y = (e.clientY / window.innerHeight - 0.5) * 15;
      setParallaxOffset({ x, y });
    };

    window.addEventListener('mousemove', handleMouseMove, { passive: true });
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // No renderizar si no hay estrellas
  if (stars.length === 0) return null;

  return (
    <div 
      className={styles.starfield}
      style={{
        '--parallax-x': `${parallaxOffset.x}px`,
        '--parallax-y': `${parallaxOffset.y}px`,
      }}
    >
      {stars.map((star) => (
        <div
          key={star.id}
          className={styles.star}
          style={{
            left: `${star.left}%`,
            top: `${star.top}%`,
            width: `${star.size}px`,
            height: `${star.size}px`,
            animationDuration: `${star.animationDuration}s`,
            animationDelay: `${star.animationDelay}s`,
          }}
        />
      ))}
    </div>
  );
};

export default React.memo(Starfield);
